// src/pages/packingBatch/components/ProductListPrintModal.jsx
import React from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import PrintPreviewModal from '../../../components/common/PrintPreviewModal/PrintPreviewModal';
import { printProductList } from '../../../utils/packingBatchProductExport';
import { formatDateTime } from '../../../utils/dateUtils';
import styles from './ProductListPrintModal.module.css';

const ProductListPrintModal = ({ products = [], batchNumber, onClose, onError }) => {
  const { t } = useLocalization();

  const totalQuantity = products.reduce((sum, p) => sum + (parseFloat(p.totalQuantity) || 0), 0);

  const handlePrint = (mode) => {
    if (mode === 'a4') {
      const result = printProductList(products, batchNumber);
      if (!result.success && result.message && onError) onError(result.message);
      return;
    }
    window.print();
  };

  const renderReceipt = () => (
    <div className={styles.receiptArea}>
      <h2 className={styles.receiptTitle}>{t('packingBatch.productListReceiptTitle')}</h2>

      <div className={styles.receiptMeta}>
        <div className={styles.receiptMetaRow}>
          <span>{t('packingBatch.productListBatchLabel')}</span>
          <span>{batchNumber}</span>
        </div>
        <div className={styles.receiptMetaRow}>
          <span>{t('packingBatch.productListPrintedOn')}</span>
          <span>{formatDateTime(new Date().toISOString())}</span>
        </div>
      </div>

      <hr className={styles.receiptDivider} />

      <div className={styles.receiptItems}>
        {products.map((product) => (
          <div key={`${product.productId}-${product.variationId || 'base'}`} className={styles.receiptItem}>
            <span className={styles.receiptItemName}>
              {product.displayName}
              {product.sku ? ` — ${product.sku}` : ''}
            </span>
            <span className={styles.receiptItemQty}>×{product.totalQuantity}</span>
          </div>
        ))}
      </div>

      <hr className={styles.receiptDivider} />

      <div className={styles.receiptMeta}>
        <div className={styles.receiptMetaRow}>
          <span>{t('packingBatch.receiptTotalProducts')}</span>
          <span>{products.length}</span>
        </div>
        <div className={`${styles.receiptMetaRow} ${styles.receiptGrandTotal}`}>
          <span>{t('packingBatch.receiptTotalQuantity')}</span>
          <span>{totalQuantity}</span>
        </div>
      </div>
    </div>
  );

  const renderA4 = () => (
    <div className={styles.a4Page}>
      <h1 className={styles.a4Title}>Product List{batchNumber ? ` — Batch #${batchNumber}` : ''}</h1>
      <p className={styles.a4Meta}>Generated: {new Date().toLocaleString()}</p>
      <p className={styles.a4Meta}>Total Products: {products.length}</p>

      <table className={styles.a4Table}>
        <thead>
          <tr>
            <th className={styles.a4ColIndex}>#</th>
            <th className={styles.a4ColProduct}>Product</th>
            <th className={styles.a4ColSku}>SKU</th>
            <th className={`${styles.a4ColQty} ${styles.a4Right}`}>Total Quantity</th>
            <th className={`${styles.a4ColOrders} ${styles.a4Right}`}>Orders</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => (
            <tr key={`${product.productId}-${product.variationId || 'base'}`}>
              <td>{index + 1}</td>
              <td>{product.displayName}</td>
              <td>{product.sku || ''}</td>
              <td className={styles.a4Right}>{product.totalQuantity}</td>
              <td className={styles.a4Right}>{product.ordersCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <PrintPreviewModal
      title={t('packingBatch.productListPrintTitle', { number: batchNumber })}
      onClose={onClose}
      onPrint={handlePrint}
      renderReceipt={renderReceipt}
      renderA4={renderA4}
    />
  );
};

export default ProductListPrintModal;
